import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, formatApiError } from '../services/api';
import { UserIcon, EnvelopeIcon, LockClosedIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Nama harus diisi';
    if (!formData.email) {
      newErrors.email = 'Email harus diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }
    if (!formData.password) {
      newErrors.password = 'Password harus diisi';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password minimal 6 karakter';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Password tidak cocok';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await authAPI.register({
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      const { user: userData, token: authToken } = response.data;
      login(userData, authToken);
      toast.success('Registrasi berhasil!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-6 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="bg-white p-8 rounded-3xl shadow-soft">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Daftar Sekarang</h2>
          <p className="text-slate-600 mb-8">Buat akun untuk mulai patungin</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Nama Lengkap</label>
              <div className="relative">
                <UserIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input type="text" name="name" data-testid="register-name-input" value={formData.name} onChange={handleChange} placeholder="Masukkan nama lengkap"
                  className={`w-full pl-12 pr-4 py-3 rounded-[16px] border ${errors.name ? 'border-danger' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary transition-all`} />
              </div>
              {errors.name && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-danger mt-1 flex items-center gap-1"><ExclamationCircleIcon className="w-4 h-4" />{errors.name}</motion.p>)}
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Email</label>
              <div className="relative">
                <EnvelopeIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input type="email" name="email" data-testid="register-email-input" value={formData.email} onChange={handleChange} placeholder="nama@email.com"
                  className={`w-full pl-12 pr-4 py-3 rounded-[16px] border ${errors.email ? 'border-danger' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary transition-all`} />
              </div>
              {errors.email && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-danger mt-1 flex items-center gap-1"><ExclamationCircleIcon className="w-4 h-4" />{errors.email}</motion.p>)}
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Password</label>
              <div className="relative">
                <LockClosedIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input type="password" name="password" data-testid="register-password-input" value={formData.password} onChange={handleChange} placeholder="Minimal 6 karakter"
                  className={`w-full pl-12 pr-4 py-3 rounded-[16px] border ${errors.password ? 'border-danger' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary transition-all`} />
              </div>
              {errors.password && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-danger mt-1 flex items-center gap-1"><ExclamationCircleIcon className="w-4 h-4" />{errors.password}</motion.p>)}
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Konfirmasi Password</label>
              <div className="relative">
                <LockClosedIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input type="password" name="confirmPassword" data-testid="register-confirm-password-input" value={formData.confirmPassword} onChange={handleChange} placeholder="Ulangi password"
                  className={`w-full pl-12 pr-4 py-3 rounded-[16px] border ${errors.confirmPassword ? 'border-danger' : 'border-border'} focus:outline-none focus:ring-2 focus:ring-primary transition-all`} />
              </div>
              {errors.confirmPassword && (<motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-xs text-danger mt-1 flex items-center gap-1"><ExclamationCircleIcon className="w-4 h-4" />{errors.confirmPassword}</motion.p>)}
            </div>

            <button type="submit" data-testid="register-submit-btn" disabled={loading}
              className="w-full bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Memproses...' : 'Daftar'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Sudah punya akun?{' '}
              <button onClick={() => navigate('/login')} data-testid="login-link" className="text-primary font-semibold hover:underline">Login di sini</button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
